import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

const SHEET_W = 816;   // letter width at 96dpi
const SHEET_H = 1056;  // letter height at 96dpi

// Renders the results block into a letter-size PDF, as the original
// calculator does: an offscreen 816px-wide clone (action buttons removed,
// option accordions expanded to their summary rows), captured with
// html2canvas and paginated onto letter pages.
export async function saveEstimateAsPdf(sourceId: string, ret: Date | null): Promise<void> {
  const source = document.getElementById(sourceId);
  if (!source) throw new Error('Estimate content not found.');

  const root = document.createElement('div');
  root.setAttribute('aria-hidden', 'true');
  root.className = 'retirement-calc retirement-results';
  root.style.cssText = `position:absolute;left:0;top:0;width:${SHEET_W}px;background:#fff;z-index:-1;pointer-events:none;`;
  const clone = source.cloneNode(true) as HTMLElement;
  clone.classList.add('pdf-mode');
  clone.querySelectorAll('.no-pdf').forEach(el => el.remove());
  // Summary rows carry the values; expanded prose is left out to keep the estimate compact.
  clone.querySelectorAll('.opt-detail').forEach(el => el.remove());
  clone.querySelectorAll('details').forEach(el => el.setAttribute('open', ''));
  root.appendChild(clone);
  document.body.appendChild(root);

  try {
    if ((document as any).fonts?.ready) { try { await (document as any).fonts.ready; } catch { /* ignore */ } }
    await new Promise(res => requestAnimationFrame(() => requestAnimationFrame(res)));

    // html2canvas mis-renders this SVG (it has only a viewBox), so the logo is
    // rasterized here and drawn straight onto page 1 of the PDF instead; the
    // clone just reserves the space.
    let logoPng: string | null = null;
    const logoImg = clone.querySelector<HTMLImageElement>('.results-logo img');
    if (logoImg) {
      try {
        const svgText = await (await fetch(logoImg.src)).text();
        const sized = svgText.replace(/<svg /, '<svg width="260" height="68" ');
        const loaded = new Image();
        await new Promise<void>((res, rej) => { loaded.onload = () => res(); loaded.onerror = () => rej(new Error('logo')); loaded.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(sized)}`; });
        const c = document.createElement('canvas');
        c.width = 260 * 3; c.height = 68 * 3;
        c.getContext('2d')!.drawImage(loaded, 0, 0, c.width, c.height);
        logoPng = c.toDataURL('image/png');
      } catch { /* PDF is still produced without the logo */ }
      logoImg.style.visibility = 'hidden';
      logoImg.style.height = '54px';
      logoImg.style.width = `${Math.round(54 * 260 / 68)}px`;
    }

    // Push any block that would straddle a page boundary onto the next page.
    const blocks = Array.from(clone.querySelectorAll<HTMLElement>('.hero-grid, .panel'));
    for (const el of blocks) {
      const top = el.getBoundingClientRect().top - root.getBoundingClientRect().top;
      const height = el.offsetHeight;
      const boundary = Math.ceil((top + 1) / SHEET_H) * SHEET_H;
      if (top + height > boundary && height < SHEET_H - 80) {
        el.style.marginTop = `${boundary - top + 32}px`;
      }
    }

    const canvas = await html2canvas(root, {
      scale: 2, useCORS: true, backgroundColor: '#ffffff', windowWidth: SHEET_W, width: SHEET_W, scrollX: 0, scrollY: 0, logging: false,
    });

    const pdf = new jsPDF({ unit: 'pt', format: 'letter', orientation: 'portrait' });
    const pageHpx = SHEET_H * 2; // canvas px per page at scale 2
    const pages = Math.max(1, Math.ceil(canvas.height / pageHpx));
    for (let i = 0; i < pages; i++) {
      const slice = document.createElement('canvas');
      slice.width = canvas.width;
      slice.height = Math.min(pageHpx, canvas.height - i * pageHpx);
      const ctx = slice.getContext('2d')!;
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, slice.width, slice.height);
      ctx.drawImage(canvas, 0, i * pageHpx, canvas.width, slice.height, 0, 0, slice.width, slice.height);
      if (i > 0) pdf.addPage('letter', 'portrait');
      pdf.addImage(slice.toDataURL('image/jpeg', 0.97), 'JPEG', 0, 0, 612, (slice.height / pageHpx) * 792, undefined, 'FAST');
      // Logo sits at the clone's 36px/32px padding origin (0.75pt per css px).
      if (i === 0 && logoPng) pdf.addImage(logoPng, 'PNG', 27, 24, 54 * 0.75 * 260 / 68, 54 * 0.75);
    }

    const d = ret ?? new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    pdf.save(`SCERS-Retirement-Estimate-${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}.pdf`);
  } finally {
    root.remove();
  }
}
